import { useCallback } from 'react';
import { useRouter } from '@funtech-int/rival-react';
import { Router } from '@funtech-int/rival';
import { useFeatureFlags } from '@funtech-int/yango-shared';

import { QueryParameter } from '@/shared/constants/queryParams';
import { setQueryParams } from '@/scenarios/legacy-player/lib/set-query-params';
import { playerRoute } from '@/scenarios/player';

import { featureFlagsScheme } from '../../../lib/featureFlags';
import { PlayerContentType } from '../../feature-player/types/content';
import { enrichPromoParamsIfNeed } from '../helpers/enrich-promo-params-if-need';
import { QueryParams } from '../types';

import { playingContentIdVar } from './use-playing-content-id-var';
import { setReadyToPlay } from './use-is-ready-to-play';

interface TvSeries {
    seasonNumber: number | undefined;
    seriesNumber: number;
}

interface StartContent {
    contentId: string;
    movieId?: string;
    tvSeries?: TvSeries;
}

interface Params {
    router: Router;
    contentId: string;
    contentType: PlayerContentType;
    tvSeries?: TvSeries;
    additionalQueryParams?: Record<string, string>;
}

const startPlayingContent = ({
    router,
    contentId,
    contentType,
    tvSeries,
    additionalQueryParams,
}: Params) => {
    setReadyToPlay(false);
    playingContentIdVar.set(contentId);

    if (contentType === PlayerContentType.EPISODE) {
        if (tvSeries?.seasonNumber) {
            const paramsToAdd: QueryParams = enrichPromoParamsIfNeed({
                [QueryParameter.WATCH]: '',
                [QueryParameter.SEASON_NUMBER]: String(tvSeries.seasonNumber),
                [QueryParameter.EPISODE_NUMBER]: String(tvSeries.seriesNumber),
                [QueryParameter.PLAYING_CONTENT_ID]: contentId,
                ...additionalQueryParams,
            });

            return setQueryParams({
                router,
                queryParams: paramsToAdd,
            });
        }
    }

    // если не найден сезон для эпизода, то устанавливаем хотя бы contentId
    const paramsToAdd: QueryParams = enrichPromoParamsIfNeed({
        [QueryParameter.WATCH]: '',
        [QueryParameter.PLAYING_CONTENT_ID]: contentId,
        ...additionalQueryParams,
    });

    return setQueryParams({
        router,
        queryParams: paramsToAdd,
    });
};

export function useStartPlayingContent() {
    const router = useRouter();
    const isNewPlayerEnabled = useFeatureFlags(featureFlagsScheme).flags.get('isNewPlayerEnabled');

    const startContent = useCallback(
        ({ tvSeries, movieId, contentId }: StartContent) => {
            let additionalQueryParams: Record<string, string> = {};

            if (isNewPlayerEnabled) {
                return router.navigate({
                    to: playerRoute({ contentId }),
                });
            }

            if (movieId) {
                additionalQueryParams = {
                    [QueryParameter.REDIRECT_TO_FILM_PAGE]: movieId,
                    [QueryParameter.CONTINUE_WATCHING]: '1',
                };
            }

            if (tvSeries) {
                startPlayingContent({
                    router,
                    contentId,
                    contentType: PlayerContentType.EPISODE,
                    tvSeries,
                    additionalQueryParams,
                });
            } else {
                startPlayingContent({
                    router,
                    contentId,
                    contentType: PlayerContentType.FILM,
                    additionalQueryParams,
                });
            }
        },
        [isNewPlayerEnabled, router]
    );

    return startContent;
}
